import React from 'react';
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FaStar, FaShoppingCart, FaHeart } from 'react-icons/fa';
import { useCart } from '../context/CartContext';

function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);

  const products = {
    '1': {
      id: 1,
      name: 'Premium Whey Protein Powder',
      shortDescription: 'High-quality protein supplement for muscle growth and recovery',
      description: 'Our Premium Whey Protein Powder is formulated with the highest quality whey protein isolate to support muscle growth, recovery, and overall fitness goals. Each serving delivers 25g of pure protein with minimal carbs and fats. Perfect for post-workout recovery or as a nutritious snack throughout the day.',
      price: 45.99,
      rating: 5,
      reviews: 234,
      image: '/images/1.jpg',
      features: [
        '25g of high-quality whey protein per serving',
        'Contains all essential amino acids including BCAAs',
        'Low in carbohydrates and fats',
        'Easy to mix and great tasting flavors',
        'No artificial colors or preservatives',
        'Suitable for post-workout recovery',
        'Supports lean muscle growth',
        'Lab tested for purity and quality'
      ]
    },
    '2': {
      id: 2,
      name: 'Professional Yoga Mat',
      shortDescription: 'Non-slip eco-friendly yoga mat with extra cushioning',
      description: 'Experience ultimate comfort and stability with our Professional Yoga Mat. Made from eco-friendly materials, this mat features superior grip technology and extra cushioning to support your practice. Whether you are a beginner or an advanced practitioner, this mat provides the perfect foundation for your yoga journey.',
      price: 32.99,
      rating: 5,
      reviews: 189,
      image: '/images/2.jpg',
      features: [
        'Extra thick 6mm cushioning for joint protection',
        'Non-slip surface for secure poses',
        'Eco-friendly TPE material',
        'Free from harmful chemicals and latex',
        'Lightweight and easy to carry',
        'Includes carrying strap',
        'Easy to clean and maintain',
        'Available in multiple colors'
      ]
    },
    '3': {
      id: 3,
      name: 'Resistance Bands Set',
      shortDescription: 'Complete set of 5 resistance bands for full body workout',
      description: 'Transform your home workout routine with our comprehensive Resistance Bands Set. This complete set includes 5 bands with varying resistance levels, perfect for strength training, stretching, and rehabilitation exercises. Compact and portable, you can take your workout anywhere.',
      price: 24.99,
      rating: 4,
      reviews: 156,
      image: '/images/3.jpg',
      features: [
        'Set of 5 bands with different resistance levels',
        'Made from premium quality latex',
        'Includes door anchor and handles',
        'Comes with carrying bag for portability',
        'Suitable for all fitness levels',
        'Perfect for home and travel workouts',
        'Targets all major muscle groups',
        'Includes exercise guide'
      ]
    },
    '4': {
      id: 4,
      name: 'Smart Fitness Tracker',
      shortDescription: 'Advanced fitness tracker with heart rate monitor and GPS',
      description: 'Stay on top of your fitness goals with our Smart Fitness Tracker. This advanced device monitors your heart rate, tracks your activities, and provides detailed insights into your health and fitness. With built-in GPS and water resistance, it is perfect for all your activities from running to swimming.',
      price: 79.99,
      rating: 5,
      reviews: 312,
      image: '/images/4.jpg',
      features: [
        '24/7 heart rate monitoring',
        'Built-in GPS for accurate tracking',
        'Water resistant up to 50 meters',
        'Sleep quality monitoring',
        'Multiple sport modes',
        'Smart notifications and alerts',
        'Long battery life up to 7 days',
        'Compatible with iOS and Android'
      ]
    },
    '5': {
      id: 5,
      name: 'Insulated Water Bottle',
      shortDescription: 'Premium stainless steel bottle keeps drinks cold for 24 hours',
      description: 'Stay hydrated throughout your day with our Premium Insulated Water Bottle. Featuring double-wall vacuum insulation, this bottle keeps your drinks cold for up to 24 hours or hot for 12 hours. Made from high-quality stainless steel, it is durable, leak-proof, and perfect for all your activities.',
      price: 18.99,
      rating: 5,
      reviews: 278,
      image: '/images/5.jpg',
      features: [
        'Double-wall vacuum insulation technology',
        'Keeps drinks cold for 24 hours',
        'Keeps drinks hot for 12 hours',
        'Made from premium stainless steel',
        'BPA-free and eco-friendly',
        'Leak-proof lid design',
        '750ml capacity perfect for workouts',
        'Wide mouth for easy cleaning'
      ]
    }
  };

  const product = products[id];

  if (!product) {
    return <div className="product-detail-container"><h1>Product not found</h1></div>;
  }

  const handleAddToCart = () => {
    addToCart(product, quantity);
    
    const notification = document.createElement('div');
    notification.className = 'notification';
    notification.textContent = 'Product added to cart successfully!';
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.remove();
    }, 3000);
  };

  const handleBuyNow = () => {
    addToCart(product, quantity);
    navigate('/checkout');
  };

  const increaseQuantity = () => {
    setQuantity(prev => prev + 1);
  };

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(prev => prev - 1);
    }
  };

  return (
    <div className="product-detail-container">
      <Helmet>
        <title>{product.name} - Fitness Health Blog</title>
        <meta name="description" content={product.description} />
        <meta property="og:title" content={product.name} />
        <meta property="og:description" content={product.description} />
        <meta property="og:image" content={'https://topfitnessproducts.com' + product.image} />
        <meta property="og:type" content="product" />
        <meta property="product:price:amount" content={product.price} />
        <meta property="product:price:currency" content="GBP" />
      </Helmet>

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Product',
          'name': product.name,
          'description': product.description,
          'image': 'https://topfitnessproducts.com' + product.image,
          'offers': {
            '@type': 'Offer',
            'price': product.price,
            'priceCurrency': 'GBP',
            'availability': 'https://schema.org/InStock'
          },
          'aggregateRating': {
            '@type': 'AggregateRating',
            'ratingValue': product.rating,
            'reviewCount': product.reviews
          }
        })}
      </script>

      <div className="product-detail-grid">
        <div>
          <img src={product.image} alt={product.name} className="product-detail-image" />
        </div>

        <div className="product-detail-info">
          <h1>{product.name}</h1>
          
          <div className="product-rating">
            {[...Array(5)].map((_, i) => (
              <FaStar key={i} size={24} color={i < product.rating ? '#ffa502' : '#ddd'} />
            ))}
            <span>({product.reviews} customer reviews)</span>
          </div>

          <div className="product-price">£{product.price.toFixed(2)}</div>

          <p className="product-description">{product.description}</p>

          <div className="quantity-selector">
            <button onClick={decreaseQuantity}>-</button>
            <span>{quantity}</span>
            <button onClick={increaseQuantity}>+</button>
          </div>

          <div className="product-actions">
            <button className="btn-primary" onClick={handleAddToCart}>
              <FaShoppingCart /> Add to Cart
            </button>
            <button className="btn-secondary" onClick={handleBuyNow}>
              <FaHeart /> Buy Now
            </button>
          </div>
        </div>
      </div>

      <div className="product-features">
        <h2>Product Features</h2>
        <ul>
          {product.features.map((feature, index) => (
            <li key={index}>{feature}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default ProductDetail;